import { AssetsIcon } from '@atlaskit/logo/assets/icon';
/**
 * First-party product configurations.
 * These are the Atlassian 1P products with their display metadata (icons, display names).
 */
import { AtlassianIcon } from '@atlaskit/logo/atlassian-icon';
import { BitbucketIcon } from '@atlaskit/logo/bitbucket-icon';
import { CompassIcon } from '@atlaskit/logo/compass/icon';
import { ConfluenceIcon } from '@atlaskit/logo/confluence-icon';
import { CustomerServiceManagementIcon } from '@atlaskit/logo/customer-service-management/icon';
import { FocusIcon } from '@atlaskit/logo/focus/icon';
import { JiraIcon } from '@atlaskit/logo/jira-icon';
import { JiraProductDiscoveryIcon } from '@atlaskit/logo/jira-product-discovery/icon';
import { JiraServiceManagementIcon } from '@atlaskit/logo/jira-service-management-icon';
import { TalentIcon } from '@atlaskit/logo/talent/icon';
import { TrelloIcon } from '@atlaskit/logo/trello-icon';
import type { EntityATI } from '@atlassian/search-client/common/types';

import { ProductKeys1P } from '../../constants/types';
import { DEFAULT_ASSETS_ENTITY_ATI } from '../../types/default-assets-entity-ati';
import { DEFAULT_ATLAS_ENTITY_ATI } from '../../types/default-atlas-entity-ati';
import { DEFAULT_BITBUCKET_ENTITY_ATI } from '../../types/default-bitbucket-entity-ati';
import { DEFAULT_COMPASS_ENTITY_ATI } from '../../types/default-compass-entity-ati';
import { DEFAULT_CONFLUENCE_ENTITY_ATI } from '../../types/default-confluence-entity-ati';
import { DEFAULT_FOCUS_ENTITY_ATI } from '../../types/default-focus-entity-ati';
import { DEFAULT_JIRA_ENTITY_ATI } from '../../types/default-jira-entity-ati';
import { DEFAULT_TALENT_ENTITY_ATI } from '../../types/default-talent-entity-ati';
import { DEFAULT_TRELLO_ENTITY_ATI } from '../../types/default-trello-entity-ati';
import { type RovoLogo, RovoLogoFromLogo, RovoLogoFromSVG } from '../icons';
import {
	AssetsSearchResultIcon,
	AtlasSearchResultIcon,
	CompassSearchResultIcon,
	ConfluenceSearchResultIcon,
	FocusSearchResultIcon,
	JiraProductDiscoverySearchResultIcon,
	TalentSearchResultIcon,
	TrelloSearchResultIcon,
} from '../icons/first-party';
import { JiraSearchResultIcon } from '../icons/jira/jira-search-result-icon';
import { JiraServiceManagementSearchResultIcon } from '../icons/jira/jira-service-management-search-result-icon';

/**
 * Configuration for a first-party (1P) Atlassian product.
 */
export interface FirstPartyConfig {
	/**
	 * The displayed name of the product.
	 */
	displayName: string;

	/**
	 * The icon for the product (from @atlaskit/logo).
	 */
	icon: RovoLogo;

	/**
	 * The search result icon for the product.
	 * Used for larger product representation in search results.
	 */
	searchResultIcon?: RovoLogo;

	/**
	 * The entity ATIs for this product.
	 * Used to build search queries targeting this product's content.
	 */
	entities?: readonly EntityATI[];
}

/**
 * Narrower type for first-party configs that always have entity ATIs defined.
 * Used as the value type of FirstPartyConfigsSync to allow consumers to access
 * entities without null-checking.
 */
export type FirstPartyConfigWithEntities = Omit<FirstPartyConfig, 'entities'> & {
	entities: readonly EntityATI[];
};

/**
 * First-party product configurations.
 * This is the source of truth for 1P product metadata.
 */
export const FirstPartyConfigs: Record<ProductKeys1P, FirstPartyConfig> = {
	[ProductKeys1P.Confluence]: {
		displayName: 'Confluence',
		icon: RovoLogoFromLogo(ConfluenceIcon, 'Confluence'),
		searchResultIcon: RovoLogoFromSVG(ConfluenceSearchResultIcon, 'Confluence', 'large'),
	},
	[ProductKeys1P.Atlas]: {
		displayName: 'Home',
		icon: RovoLogoFromLogo(AtlassianIcon, 'Home'),
		searchResultIcon: RovoLogoFromSVG(AtlasSearchResultIcon, 'Home', 'large'),
	},
	[ProductKeys1P.Jira]: {
		displayName: 'Jira',
		icon: RovoLogoFromLogo(JiraIcon, 'Jira'),
		searchResultIcon: RovoLogoFromSVG(JiraSearchResultIcon, 'Jira', 'large'),
	},
	[ProductKeys1P.JiraProductDiscovery]: {
		displayName: 'Jira Product Discovery',
		icon: RovoLogoFromLogo(JiraProductDiscoveryIcon, 'Jira Product Discovery'),
		searchResultIcon: RovoLogoFromSVG(
			JiraProductDiscoverySearchResultIcon,
			'Jira Product Discovery',
			'large',
		),
	},
	[ProductKeys1P.JiraServiceManagement]: {
		displayName: 'Jira Service Management',
		icon: RovoLogoFromLogo(JiraServiceManagementIcon, 'Jira Service Management'),
		searchResultIcon: RovoLogoFromSVG(
			JiraServiceManagementSearchResultIcon,
			'Jira Service Management',
			'large',
		),
	},
	[ProductKeys1P.CustomerServiceManagement]: {
		displayName: 'Customer Service Management',
		icon: RovoLogoFromLogo(CustomerServiceManagementIcon, 'Customer Service Management'),
		searchResultIcon: RovoLogoFromLogo(
			CustomerServiceManagementIcon,
			'Customer Service Management',
		),
	},
	[ProductKeys1P.Bitbucket]: {
		displayName: 'Bitbucket',
		icon: RovoLogoFromLogo(BitbucketIcon, 'Bitbucket'),
		// Bitbucket uses the same icon for search results
		searchResultIcon: RovoLogoFromLogo(BitbucketIcon, 'Bitbucket'),
	},
	[ProductKeys1P.Trello]: {
		displayName: 'Trello',
		icon: RovoLogoFromLogo(TrelloIcon, 'Trello'),
		searchResultIcon: RovoLogoFromSVG(TrelloSearchResultIcon, 'Trello', 'large'),
	},
	[ProductKeys1P.Compass]: {
		displayName: 'Compass',
		icon: RovoLogoFromLogo(CompassIcon, 'Compass'),
		searchResultIcon: RovoLogoFromSVG(CompassSearchResultIcon, 'Compass', 'large'),
	},
	[ProductKeys1P.Focus]: {
		displayName: 'Focus',
		icon: RovoLogoFromLogo(FocusIcon, 'Focus'),
		searchResultIcon: RovoLogoFromSVG(FocusSearchResultIcon, 'Focus', 'large'),
	},
	[ProductKeys1P.Assets]: {
		displayName: 'Assets',
		icon: RovoLogoFromLogo(AssetsIcon, 'Assets'),
		searchResultIcon: RovoLogoFromSVG(AssetsSearchResultIcon, 'Assets', 'large'),
	},
	[ProductKeys1P.Talent]: {
		displayName: 'Talent',
		icon: RovoLogoFromLogo(TalentIcon, 'Talent'),
		searchResultIcon: RovoLogoFromSVG(TalentSearchResultIcon, 'Talent', 'large'),
	},
};

/**
 * Product ordering for first-party (1P) Atlassian products.
 * Controls the display order of 1P products in the search interface.
 */
export const ProductsOrder1P: ProductKeys1P[] = [
	ProductKeys1P.Confluence,
	ProductKeys1P.Jira,
	ProductKeys1P.JiraProductDiscovery,
	ProductKeys1P.JiraServiceManagement,
	ProductKeys1P.CustomerServiceManagement,
	ProductKeys1P.Atlas,
	ProductKeys1P.Trello,
	ProductKeys1P.Bitbucket,
	ProductKeys1P.Compass,
	ProductKeys1P.Focus,
	ProductKeys1P.Assets,
	ProductKeys1P.Talent,
];

/**
 * Get the configuration for a specific 1P product.
 * @param productKey The product key
 * @returns The product configuration, or undefined if not found
 */
export const getFirstPartyConfig = (productKey: ProductKeys1P): FirstPartyConfig | undefined => {
	return FirstPartyConfigs[productKey];
};

/**
 * Synchronous Map of first-party product configurations with entity ATIs, keyed by ProductKeys1P.
 * Complementary to ThirdPartyConfigsSync — use this as the source of truth for 1P search query entities.
 *
 * Note: JiraProductDiscovery, JiraServiceManagement, and CustomerServiceManagement are intentionally
 * excluded — they do not have independent searchable entity ATI types and their results surface under
 * the core Jira product.
 */
export const FirstPartyConfigsSync: ReadonlyMap<ProductKeys1P, FirstPartyConfigWithEntities> =
	new Map<ProductKeys1P, FirstPartyConfigWithEntities>([
		[
			ProductKeys1P.Confluence,
			{
				...FirstPartyConfigs[ProductKeys1P.Confluence],
				entities: DEFAULT_CONFLUENCE_ENTITY_ATI,
			},
		],
		[
			ProductKeys1P.Jira,
			{
				...FirstPartyConfigs[ProductKeys1P.Jira],
				entities: DEFAULT_JIRA_ENTITY_ATI,
			},
		],
		[
			ProductKeys1P.Atlas,
			{
				...FirstPartyConfigs[ProductKeys1P.Atlas],
				entities: DEFAULT_ATLAS_ENTITY_ATI,
			},
		],
		[
			ProductKeys1P.Compass,
			{
				...FirstPartyConfigs[ProductKeys1P.Compass],
				entities: DEFAULT_COMPASS_ENTITY_ATI,
			},
		],
		[
			ProductKeys1P.Trello,
			{
				...FirstPartyConfigs[ProductKeys1P.Trello],
				entities: DEFAULT_TRELLO_ENTITY_ATI,
			},
		],
		[
			ProductKeys1P.Bitbucket,
			{
				...FirstPartyConfigs[ProductKeys1P.Bitbucket],
				entities: DEFAULT_BITBUCKET_ENTITY_ATI,
			},
		],
		[
			ProductKeys1P.Assets,
			{
				...FirstPartyConfigs[ProductKeys1P.Assets],
				entities: DEFAULT_ASSETS_ENTITY_ATI,
			},
		],
		[
			ProductKeys1P.Talent,
			{
				...FirstPartyConfigs[ProductKeys1P.Talent],
				entities: DEFAULT_TALENT_ENTITY_ATI,
			},
		],
		[
			ProductKeys1P.Focus,
			{
				...FirstPartyConfigs[ProductKeys1P.Focus],
				entities: DEFAULT_FOCUS_ENTITY_ATI,
			},
		],
	]);
